import { getErrorMessage } from "@helper/getErrorMessage";
import { isArrayWithElements } from "@helper/utils";
import { ProcessedArticleData } from "@hooks/transformer/useOSResponse";
import type {
  FetchMoreArticlesFunction,
  MoreArticlesRequestData,
} from "@typings/MoreArticlesApi";
import React from "react";

import { increasePagination } from "./IncreasePagination";
import { UpdateGADataForArticle } from "./UpdateGADataForArticle";

interface LoadMoreHandlerParams {
  lastLoadedArticle: ProcessedArticleData | undefined;
  loadMoreData: ProcessedArticleData[];
  renderedArticleIds: Set<string>;
  pageSize: number;
  pageNumber: number;
}

export type CreateLoadMoreHandlerProps = {
  requestData: MoreArticlesRequestData;
  fetchFunction: FetchMoreArticlesFunction;
  setHasMoreStories: React.Dispatch<React.SetStateAction<boolean>>;
  setRenderedArticleIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setLoadMoreData: React.Dispatch<React.SetStateAction<ProcessedArticleData[]>>;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  updateGADataForArticle?: boolean;
} & LoadMoreHandlerParams;

export async function CreateLoadMoreHandler({
  requestData,
  fetchFunction,
  setHasMoreStories,
  setRenderedArticleIds,
  setLoadMoreData,
  setPageNumber,
  lastLoadedArticle,
  loadMoreData,
  renderedArticleIds,
  pageSize,
  pageNumber,
  updateGADataForArticle = false,
}: CreateLoadMoreHandlerProps): Promise<ProcessedArticleData[]> {
  let moreArticles = [] as ProcessedArticleData[];
  try {
    const lastArticle =
      loadMoreData.length === 0 ? lastLoadedArticle : loadMoreData.at(-1);

    if (lastArticle) {
      const newArticles: ProcessedArticleData[] = [];
      moreArticles = await fetchFunction(requestData);
      moreArticles.forEach((article: ProcessedArticleData) => {
        if (!renderedArticleIds.has(article.id)) {
          newArticles.push(article);
          setRenderedArticleIds(
            (prevState) => new Set([...prevState, article.id]),
          );
        }
      });
      setLoadMoreData([...loadMoreData, ...newArticles]);
      if (isArrayWithElements(moreArticles)) {
        setPageNumber((prevPageNumber) => prevPageNumber + 1);
        increasePagination((pageNumber + 1).toString());
        updateGADataForArticle && UpdateGADataForArticle(moreArticles[0]);
      }
      if (newArticles.length < pageSize) {
        setHasMoreStories(false);
      }
    }
  } catch (error) {
    const err = getErrorMessage(error);
    console.error("Something went wrong", err);
  }
  return moreArticles;
}
